import { createContext, useContext, useEffect, useState } from "react";
import { NavLink, Navigate, Route, Routes, useNavigate } from "react-router-dom";
import { Home, FileText, Files, UserCircle, ShieldCheck } from "lucide-react";
import { api } from "../../lib/api";
import { useAuth } from "../../lib/auth";
import { t } from "../../lib/portalI18n";
import PortalOverview from "./PortalOverview";
import PortalClauses from "./PortalClauses";
import PortalDocuments from "./PortalDocuments";
import PortalMe from "./PortalMe";
import { FeedbackButton } from "../../components/FeedbackButton";
import { InstallPrompt } from "../../pwa/InstallPrompt";

const PortalCtx = createContext(null);
export const usePortal = () => useContext(PortalCtx);

export default function PortalShell() {
  const { user, authChecked, logout } = useAuth();
  const navigate = useNavigate();
  const [me, setMe] = useState(null);
  const [error, setError] = useState("");
  const lang = me?.lang || user?.lang || "nl";

  useEffect(() => {
    if (!authChecked || !user) return;
    api.get("/portal/me").then((r) => setMe(r.data)).catch(async (e) => {
      if (e?.response?.status === 401 || e?.response?.status === 403) { setError(t(lang, "revoked")); await logout(); navigate("/login", { replace: true }); }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authChecked, user]);

  useEffect(() => {
    const id = api.interceptors.response.use((r) => r, async (e) => {
      if (e?.response?.status === 401 && (e.config?.url || "").startsWith("/portal")) { await logout(); navigate("/login", { replace: true }); }
      return Promise.reject(e);
    });
    return () => api.interceptors.response.eject(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!authChecked) return null;
  if (!user) return <Navigate to="/login" replace />;
  if (!["player", "guardian"].includes(user.role)) return <Navigate to="/" replace />;
  if (!me) return <div className="pm-app-root flex items-center justify-center text-sm text-slate-500" data-testid="portal-loading">{error || t(lang, "loading")}</div>;

  const setLang = async (l) => { await api.put("/portal/me", { lang: l }); setMe({ ...me, lang: l }); };
  const tabs = [
    { to: "/portal", end: true, icon: Home, label: t(lang, "overview"), id: "overview" },
    { to: "/portal/clauses", icon: FileText, label: t(lang, "clauses"), id: "clauses" },
    { to: "/portal/documents", icon: Files, label: t(lang, "documents"), id: "documents" },
    { to: "/portal/me", icon: UserCircle, label: t(lang, "me"), id: "me" },
  ];

  return (
    <PortalCtx.Provider value={{ me, lang, setLang, logout }}>
      <div className="pm-app-root bg-[#f6f7f4] text-slate-900 font-sans" data-testid="portal-shell">
        <header className="sticky top-0 z-20 bg-[#0f1c17] text-white px-5 pt-[calc(1.25rem+var(--sat))] pb-4 shadow-[0_8px_30px_-15px_rgba(0,0,0,.6)]">
          <div className="max-w-xl mx-auto flex items-center gap-3">
            {me.player.photo_url ? <img src={me.player.photo_url} alt="" className="w-11 h-11 rounded-full object-cover ring-2 ring-emerald-400/60" /> : <div className="w-11 h-11 rounded-full bg-emerald-500/20 ring-2 ring-emerald-400/60 flex items-center justify-center font-bold">{me.player.full_name?.[0]}</div>}
            <div className="min-w-0 flex-1">
              <div className="text-[11px] uppercase tracking-[0.18em] text-emerald-300/90">{me.relation === "guardian" ? t(lang, "guardian_of") : "Playmaker"}</div>
              <div className="font-semibold text-lg leading-tight truncate" data-testid="portal-player-name">{me.player.full_name}</div>
              <div className="text-xs text-white/60 truncate">{[me.player.position, me.player.current_club].filter(Boolean).join(" · ")}</div>
            </div>
            <div className="text-[10px] px-2 py-1 rounded-full bg-white/10 text-white/70 flex items-center gap-1" data-testid="portal-readonly-badge"><ShieldCheck size={11} /> {t(lang, "read_only")}</div>
          </div>
        </header>
        <main className="max-w-xl mx-auto px-4 pt-5 pb-[calc(var(--pm-nav-h)+var(--sab)+16px)]">
          <InstallPrompt userId={user?.id} lang={lang} className="mb-5" />
          <Routes>
            <Route index element={<PortalOverview />} />
            <Route path="clauses" element={<PortalClauses />} />
            <Route path="documents" element={<PortalDocuments />} />
            <Route path="me" element={<PortalMe />} />
            <Route path="*" element={<Navigate to="/portal" replace />} />
          </Routes>
        </main>
        <FeedbackButton lang={lang} />
        <nav className="fixed bottom-0 inset-x-0 z-20 bg-white/95 backdrop-blur border-t border-slate-200 pb-[var(--sab)]" style={{ height: "calc(var(--pm-nav-h) + var(--sab))" }} data-testid="portal-tabbar">
          <div className="max-w-xl mx-auto grid grid-cols-4" style={{ height: "var(--pm-nav-h)" }}>
            {tabs.map((tab) => (
              <NavLink key={tab.id} to={tab.to} end={tab.end} data-testid={`portal-tab-${tab.id}`}
                className={({ isActive }) => `flex flex-col items-center justify-center gap-1 text-[11px] font-medium transition-colors ${isActive ? "text-emerald-700" : "text-slate-400 hover:text-slate-700"}`}>
                <tab.icon size={20} strokeWidth={2.2} /> {tab.label}
              </NavLink>
            ))}
          </div>
        </nav>
      </div>
    </PortalCtx.Provider>
  );
}
